/**
 * SVG → vector glyphs for the Stamp tool.
 *
 * Parses an SVG string into flat `PathData` glyphs, baking every element/group
 * `transform` into anchor coordinates so downstream normalization can place each
 * glyph independently. No layout APIs (getCTM/getBBox/getTotalLength) are used —
 * transforms are parsed and composed by hand.
 */
import type { PathData, PathSegment, Point } from './scene';
import { ellipticalArcToCubics, transformPath } from './geometry';

export interface GlyphSource {
  path: PathData;
  fillRule: 'nonzero' | 'evenodd';
  strokeOnly: boolean;
}

/** Control-point factor for approximating a quarter-circle with a cubic bezier. */
const KAPPA = 0.5522847498307936;

/** Max segments a single path may emit before we treat it as a DoS attempt. */
const MAX_SEGMENTS = 4000;
/** Max glyphs collected from one document. */
const MAX_GLYPHS = 400;
/** Bounds on document traversal (untrusted-SVG DoS / stack protection). */
const MAX_DEPTH = 800;
const MAX_NODES = 50000;

/** A 2x3 affine matrix [a, b, c, d, e, f]; point' = (a*x + c*y + e, b*x + d*y + f). */
type Matrix = [number, number, number, number, number, number];

const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

/** Compose two affine matrices: result applies `n` first, then `m` (m · n). */
function multiply(m: Matrix, n: Matrix): Matrix {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

// ---------------------------------------------------------------------------
// Number / token parsing
// ---------------------------------------------------------------------------

/**
 * A cursor over a coordinate string that yields SVG path numbers. Handles the
 * tricky cases: exponents (1e-3), signs acting as separators (1-2 → 1, -2),
 * runs of decimals (1.5.5 → 1.5, .5), and leading-dot decimals (.5). Commas and
 * whitespace are separators.
 */
class NumberReader {
  private i = 0;
  constructor(private readonly s: string) {}

  private skipSep(): void {
    while (this.i < this.s.length) {
      const c = this.s[this.i];
      if (c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === ',') this.i++;
      else break;
    }
  }

  hasMore(): boolean {
    this.skipSep();
    return this.i < this.s.length;
  }

  /** Read one number; a sign mid-stream ends the previous number and starts this one. */
  readNumber(): number {
    this.skipSep();
    const start = this.i;
    let seenDigit = false;
    let seenDot = false;
    let seenExp = false;
    // Leading sign.
    if (this.s[this.i] === '+' || this.s[this.i] === '-') this.i++;
    while (this.i < this.s.length) {
      const c = this.s[this.i];
      if (c >= '0' && c <= '9') {
        seenDigit = true;
        this.i++;
      } else if (c === '.' && !seenDot && !seenExp) {
        seenDot = true;
        this.i++;
      } else if ((c === 'e' || c === 'E') && seenDigit && !seenExp) {
        seenExp = true;
        this.i++;
        if (this.s[this.i] === '+' || this.s[this.i] === '-') this.i++;
      } else {
        break;
      }
    }
    const text = this.s.slice(start, this.i);
    const value = Number(text);
    if (text.length === 0 || Number.isNaN(value)) {
      throw new Error('Invalid SVG path number');
    }
    return value;
  }

  /**
   * Read a single arc flag (0 or 1). Flags are packed: in `a25 25 0 11 50 0`
   * the "11" is largeArc=1 + sweep=1 with no separator, so we consume exactly
   * one character and require it to be 0 or 1.
   */
  readFlag(): boolean {
    this.skipSep();
    const c = this.s[this.i];
    if (c === '0') {
      this.i++;
      return false;
    }
    if (c === '1') {
      this.i++;
      return true;
    }
    throw new Error('Invalid SVG arc flag');
  }
}

/** Split a `points`-style list ("x1,y1 x2,y2 ...") into Points. */
function parsePointList(raw: string): Point[] {
  const reader = new NumberReader(raw);
  const pts: Point[] = [];
  while (reader.hasMore()) {
    const x = reader.readNumber();
    if (!reader.hasMore()) break;
    const y = reader.readNumber();
    pts.push({ x, y });
  }
  return pts;
}

function num(raw: string | null, fallback = 0): number {
  if (raw == null || raw.trim() === '') return fallback;
  const v = parseFloat(raw); // tolerate a unit suffix like "10px" / "5mm" (treated as user units)
  return Number.isFinite(v) ? v : fallback;
}

// ---------------------------------------------------------------------------
// Path `d` parsing
// ---------------------------------------------------------------------------

/**
 * Parse an SVG path `d` string into a flat `PathData`. Supports absolute and
 * relative commands (M/L/H/V/C/S/Q/T/A/Z); H/V become L, S/T reflect the prior
 * control point, A is flattened to cubics via `ellipticalArcToCubics`.
 */
export function parsePathD(d: string): PathData {
  const segments: PathSegment[] = [];

  let curX = 0;
  let curY = 0;
  let startX = 0; // current subpath start, for Z
  let startY = 0;
  // Last control point of the previous cubic/quad, for S/T reflection.
  let prevCubicCtrl: Point | null = null;
  let prevQuadCtrl: Point | null = null;
  let prevCmd = '';

  const push = (seg: PathSegment): void => {
    segments.push(seg);
    if (segments.length > MAX_SEGMENTS) throw new Error('SVG path too complex');
  };

  const letterRe = /[MmLlHhVvCcSsQqTtAaZz]/;

  // Walk the string, dispatching on each command letter and consuming the
  // correct number of operands per command (repeating for implicit repeats).
  let idx = 0;
  const str = d;
  while (idx < str.length) {
    const ch = str[idx];
    if (!letterRe.test(ch)) {
      idx++;
      continue;
    }
    idx++;
    const cmd = ch;
    const rel = cmd === cmd.toLowerCase() && cmd !== cmd.toUpperCase();
    // Build a sub-reader for the operand run up to (but not including) the next
    // command letter.
    let j = idx;
    while (j < str.length && !letterRe.test(str[j])) j++;
    const operandStr = str.slice(idx, j);
    idx = j;
    const r = new NumberReader(operandStr);

    const upper = cmd.toUpperCase();

    if (upper === 'Z') {
      push({ t: 'Z' });
      curX = startX;
      curY = startY;
      prevCubicCtrl = null;
      prevQuadCtrl = null;
      prevCmd = upper;
      continue;
    }

    let first = true;
    while (r.hasMore()) {
      switch (upper) {
        case 'M': {
          const x = r.readNumber();
          const y = r.readNumber();
          const nx = rel ? curX + x : x;
          const ny = rel ? curY + y : y;
          if (first) {
            push({ t: 'M', p: { x: nx, y: ny } });
            startX = nx;
            startY = ny;
          } else {
            // Extra pairs after an M are implicit L (l for relative m).
            push({ t: 'L', p: { x: nx, y: ny } });
          }
          curX = nx;
          curY = ny;
          prevCubicCtrl = null;
          prevQuadCtrl = null;
          break;
        }
        case 'L': {
          const x = r.readNumber();
          const y = r.readNumber();
          const nx = rel ? curX + x : x;
          const ny = rel ? curY + y : y;
          push({ t: 'L', p: { x: nx, y: ny } });
          curX = nx;
          curY = ny;
          prevCubicCtrl = null;
          prevQuadCtrl = null;
          break;
        }
        case 'H': {
          const x = r.readNumber();
          const nx = rel ? curX + x : x;
          push({ t: 'L', p: { x: nx, y: curY } }); // fill missing axis from current
          curX = nx;
          prevCubicCtrl = null;
          prevQuadCtrl = null;
          break;
        }
        case 'V': {
          const y = r.readNumber();
          const ny = rel ? curY + y : y;
          push({ t: 'L', p: { x: curX, y: ny } }); // fill missing axis from current
          curY = ny;
          prevCubicCtrl = null;
          prevQuadCtrl = null;
          break;
        }
        case 'C': {
          const c1x = r.readNumber();
          const c1y = r.readNumber();
          const c2x = r.readNumber();
          const c2y = r.readNumber();
          const px = r.readNumber();
          const py = r.readNumber();
          const c1 = { x: rel ? curX + c1x : c1x, y: rel ? curY + c1y : c1y };
          const c2 = { x: rel ? curX + c2x : c2x, y: rel ? curY + c2y : c2y };
          const p = { x: rel ? curX + px : px, y: rel ? curY + py : py };
          push({ t: 'C', c1, c2, p });
          curX = p.x;
          curY = p.y;
          prevCubicCtrl = c2;
          prevQuadCtrl = null;
          break;
        }
        case 'S': {
          const c2x = r.readNumber();
          const c2y = r.readNumber();
          const px = r.readNumber();
          const py = r.readNumber();
          // First control is the reflection of the previous cubic's 2nd control
          // about the current point; if no prior cubic, it equals the current point.
          const reflect =
            prevCubicCtrl && (prevCmd === 'C' || prevCmd === 'S')
              ? { x: 2 * curX - prevCubicCtrl.x, y: 2 * curY - prevCubicCtrl.y }
              : { x: curX, y: curY };
          const c2 = { x: rel ? curX + c2x : c2x, y: rel ? curY + c2y : c2y };
          const p = { x: rel ? curX + px : px, y: rel ? curY + py : py };
          push({ t: 'C', c1: reflect, c2, p });
          curX = p.x;
          curY = p.y;
          prevCubicCtrl = c2;
          prevQuadCtrl = null;
          break;
        }
        case 'Q': {
          const cx = r.readNumber();
          const cy = r.readNumber();
          const px = r.readNumber();
          const py = r.readNumber();
          const c = { x: rel ? curX + cx : cx, y: rel ? curY + cy : cy };
          const p = { x: rel ? curX + px : px, y: rel ? curY + py : py };
          push({ t: 'Q', c, p });
          curX = p.x;
          curY = p.y;
          prevQuadCtrl = c;
          prevCubicCtrl = null;
          break;
        }
        case 'T': {
          const px = r.readNumber();
          const py = r.readNumber();
          // Control is the reflection of the previous quad's control about the
          // current point; if no prior quad, it equals the current point.
          const reflect: Point =
            prevQuadCtrl && (prevCmd === 'Q' || prevCmd === 'T')
              ? { x: 2 * curX - prevQuadCtrl.x, y: 2 * curY - prevQuadCtrl.y }
              : { x: curX, y: curY };
          const p = { x: rel ? curX + px : px, y: rel ? curY + py : py };
          push({ t: 'Q', c: reflect, p });
          curX = p.x;
          curY = p.y;
          prevQuadCtrl = reflect;
          prevCubicCtrl = null;
          break;
        }
        case 'A': {
          const rx = r.readNumber();
          const ry = r.readNumber();
          const rot = r.readNumber();
          // largeArc and sweep are single-digit packed flags, not full numbers.
          const largeArc = r.readFlag();
          const sweep = r.readFlag();
          const ex = r.readNumber();
          const ey = r.readNumber();
          const endX = rel ? curX + ex : ex;
          const endY = rel ? curY + ey : ey;
          const cubics = ellipticalArcToCubics(curX, curY, rx, ry, rot, largeArc, sweep, endX, endY);
          for (const seg of cubics) push(seg);
          curX = endX;
          curY = endY;
          prevCubicCtrl = null;
          prevQuadCtrl = null;
          break;
        }
        default:
          throw new Error('Unknown SVG path command');
      }
      // After the first iteration, repeats of M behave as L (handled above via `first`).
      prevCmd = upper === 'M' && first ? 'M' : upper;
      first = false;
    }
    prevCmd = upper;
  }

  return { segments };
}

// ---------------------------------------------------------------------------
// Shape → PathData builders
// ---------------------------------------------------------------------------

/** Append a cubic-bezier ellipse (4 quadrants) centred at (cx, cy). */
function ellipseSegments(cx: number, cy: number, rx: number, ry: number): PathSegment[] {
  const kx = KAPPA * rx;
  const ky = KAPPA * ry;
  return [
    { t: 'M', p: { x: cx + rx, y: cy } },
    { t: 'C', c1: { x: cx + rx, y: cy + ky }, c2: { x: cx + kx, y: cy + ry }, p: { x: cx, y: cy + ry } },
    { t: 'C', c1: { x: cx - kx, y: cy + ry }, c2: { x: cx - rx, y: cy + ky }, p: { x: cx - rx, y: cy } },
    { t: 'C', c1: { x: cx - rx, y: cy - ky }, c2: { x: cx - kx, y: cy - ry }, p: { x: cx, y: cy - ry } },
    { t: 'C', c1: { x: cx + kx, y: cy - ry }, c2: { x: cx + rx, y: cy - ky }, p: { x: cx + rx, y: cy } },
    { t: 'Z' },
  ];
}

/** Build a rounded-or-sharp rectangle path. */
function rectPath(x: number, y: number, w: number, h: number, rx: number, ry: number): PathData {
  if (w <= 0 || h <= 0) return { segments: [] };
  // Clamp / normalize corner radii per the SVG spec.
  let crx = rx;
  let cry = ry;
  if (!Number.isFinite(crx) || crx < 0) crx = 0;
  if (!Number.isFinite(cry) || cry < 0) cry = 0;
  if (crx > w / 2) crx = w / 2;
  if (cry > h / 2) cry = h / 2;

  if (crx <= 0 || cry <= 0) {
    // Sharp corners.
    return {
      segments: [
        { t: 'M', p: { x, y } },
        { t: 'L', p: { x: x + w, y } },
        { t: 'L', p: { x: x + w, y: y + h } },
        { t: 'L', p: { x, y: y + h } },
        { t: 'Z' },
      ],
    };
  }

  const segs: PathSegment[] = [];
  segs.push({ t: 'M', p: { x: x + crx, y } });
  segs.push({ t: 'L', p: { x: x + w - crx, y } });
  // top-right corner
  for (const s of ellipticalArcToCubics(x + w - crx, y, crx, cry, 0, false, true, x + w, y + cry)) segs.push(s);
  segs.push({ t: 'L', p: { x: x + w, y: y + h - cry } });
  // bottom-right corner
  for (const s of ellipticalArcToCubics(x + w, y + h - cry, crx, cry, 0, false, true, x + w - crx, y + h)) segs.push(s);
  segs.push({ t: 'L', p: { x: x + crx, y: y + h } });
  // bottom-left corner
  for (const s of ellipticalArcToCubics(x + crx, y + h, crx, cry, 0, false, true, x, y + h - cry)) segs.push(s);
  segs.push({ t: 'L', p: { x, y: y + cry } });
  // top-left corner
  for (const s of ellipticalArcToCubics(x, y + cry, crx, cry, 0, false, true, x + crx, y)) segs.push(s);
  segs.push({ t: 'Z' });
  return { segments: segs };
}

/** Build the raw (untransformed) `PathData` for one geometry leaf element. */
function shapeToPath(el: Element): PathData {
  const tag = el.tagName.toLowerCase();
  switch (tag) {
    case 'path':
      return parsePathD(el.getAttribute('d') ?? '');
    case 'rect': {
      const x = num(el.getAttribute('x'));
      const y = num(el.getAttribute('y'));
      const w = num(el.getAttribute('width'));
      const h = num(el.getAttribute('height'));
      const rxRaw = el.getAttribute('rx');
      const ryRaw = el.getAttribute('ry');
      // Per spec: if one of rx/ry is omitted, it mirrors the other.
      let rx = rxRaw != null ? num(rxRaw) : NaN;
      let ry = ryRaw != null ? num(ryRaw) : NaN;
      if (Number.isNaN(rx) && !Number.isNaN(ry)) rx = ry;
      if (Number.isNaN(ry) && !Number.isNaN(rx)) ry = rx;
      if (Number.isNaN(rx)) rx = 0;
      if (Number.isNaN(ry)) ry = 0;
      return rectPath(x, y, w, h, rx, ry);
    }
    case 'circle': {
      const cx = num(el.getAttribute('cx'));
      const cy = num(el.getAttribute('cy'));
      const r = num(el.getAttribute('r'));
      if (r <= 0) return { segments: [] };
      return { segments: ellipseSegments(cx, cy, r, r) };
    }
    case 'ellipse': {
      const cx = num(el.getAttribute('cx'));
      const cy = num(el.getAttribute('cy'));
      const rx = num(el.getAttribute('rx'));
      const ry = num(el.getAttribute('ry'));
      if (rx <= 0 || ry <= 0) return { segments: [] };
      return { segments: ellipseSegments(cx, cy, rx, ry) };
    }
    case 'line': {
      const x1 = num(el.getAttribute('x1'));
      const y1 = num(el.getAttribute('y1'));
      const x2 = num(el.getAttribute('x2'));
      const y2 = num(el.getAttribute('y2'));
      return {
        segments: [
          { t: 'M', p: { x: x1, y: y1 } },
          { t: 'L', p: { x: x2, y: y2 } },
        ],
      };
    }
    case 'polyline':
    case 'polygon': {
      const pts = parsePointList(el.getAttribute('points') ?? '');
      if (pts.length === 0) return { segments: [] };
      const segs: PathSegment[] = [{ t: 'M', p: pts[0] }];
      for (let i = 1; i < pts.length; i++) segs.push({ t: 'L', p: pts[i] });
      if (tag === 'polygon') segs.push({ t: 'Z' });
      return { segments: segs };
    }
    default:
      return { segments: [] };
  }
}

// ---------------------------------------------------------------------------
// Transform parsing
// ---------------------------------------------------------------------------

const DEG = Math.PI / 180;

/** Parse one element's `transform` attribute into a single matrix (left-to-right). */
function parseTransformAttr(value: string | null): Matrix {
  if (!value) return IDENTITY;
  let m: Matrix = IDENTITY;
  const fnRe = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
  let match: RegExpExecArray | null;
  while ((match = fnRe.exec(value)) !== null) {
    const fn = match[1];
    const args: number[] = [];
    const reader = new NumberReader(match[2]);
    while (reader.hasMore()) args.push(reader.readNumber());
    let t: Matrix = IDENTITY;
    switch (fn) {
      case 'matrix':
        if (args.length >= 6) t = [args[0], args[1], args[2], args[3], args[4], args[5]];
        break;
      case 'translate':
        t = [1, 0, 0, 1, args[0] ?? 0, args[1] ?? 0];
        break;
      case 'scale': {
        const sx = args[0] ?? 1;
        const sy = args.length >= 2 ? args[1] : sx;
        t = [sx, 0, 0, sy, 0, 0];
        break;
      }
      case 'rotate': {
        const a = (args[0] ?? 0) * DEG;
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        const rot: Matrix = [cos, sin, -sin, cos, 0, 0];
        if (args.length >= 3) {
          const cx = args[1];
          const cy = args[2];
          // translate(cx,cy) · rotate · translate(-cx,-cy)
          t = multiply(multiply([1, 0, 0, 1, cx, cy], rot), [1, 0, 0, 1, -cx, -cy]);
        } else {
          t = rot;
        }
        break;
      }
      case 'skewX':
        t = [1, 0, Math.tan((args[0] ?? 0) * DEG), 1, 0, 0];
        break;
      case 'skewY':
        t = [1, Math.tan((args[0] ?? 0) * DEG), 0, 1, 0, 0];
        break;
    }
    // Functions within one attribute compose left-to-right: m := m · t.
    m = multiply(m, t);
  }
  return m;
}

/**
 * Compose the full matrix for a leaf: walk from the element up to the root,
 * collecting each ancestor's transform. The final point transform must be
 * root · ... · parent · self, so we multiply outward as we ascend.
 */
function composeMatrix(leaf: Element): Matrix {
  let m: Matrix = IDENTITY;
  let node: Element | null = leaf;
  while (node) {
    const local = parseTransformAttr(node.getAttribute('transform'));
    // Ascending: ancestor-most ends up on the left, applied last to a point.
    m = multiply(local, m);
    const parent: Node | null = node.parentNode;
    node = parent && parent.nodeType === 1 ? (parent as Element) : null;
  }
  return m;
}

function applyMatrix(path: PathData, m: Matrix): PathData {
  const [a, b, c, d, e, f] = m;
  return transformPath(path, (p) => ({ x: a * p.x + c * p.y + e, y: b * p.x + d * p.y + f }));
}

// ---------------------------------------------------------------------------
// Paint resolution (fill-rule / fill / stroke)
// ---------------------------------------------------------------------------

/** Pull a property out of an inline `style="a:b; c:d"` string. */
function styleProp(el: Element, prop: string): string | null {
  const style = el.getAttribute('style');
  if (!style) return null;
  for (const decl of style.split(';')) {
    const idx = decl.indexOf(':');
    if (idx < 0) continue;
    const name = decl.slice(0, idx).trim();
    if (name === prop) return decl.slice(idx + 1).trim();
  }
  return null;
}

/** Resolve a presentation property, walking up ancestors until found. */
function resolveProp(el: Element, prop: string): string | null {
  let node: Element | null = el;
  while (node) {
    // Inline style wins over a same-element presentation attribute (CSS cascade).
    const styled = styleProp(node, prop);
    if (styled != null && styled !== '') return styled;
    const attr = node.getAttribute(prop);
    if (attr != null && attr.trim() !== '') return attr.trim();
    const parent: Node | null = node.parentNode;
    node = parent && parent.nodeType === 1 ? (parent as Element) : null;
  }
  return null;
}

function resolveFillRule(el: Element): 'nonzero' | 'evenodd' {
  const v = resolveProp(el, 'fill-rule');
  return v === 'evenodd' ? 'evenodd' : 'nonzero';
}

function resolveStrokeOnly(el: Element): boolean {
  const fill = resolveProp(el, 'fill');
  const stroke = resolveProp(el, 'stroke');
  const fillIsNone = fill != null && fill.toLowerCase() === 'none';
  const hasStroke = stroke != null && stroke.toLowerCase() !== 'none';
  return fillIsNone && hasStroke;
}

// ---------------------------------------------------------------------------
// Document walk
// ---------------------------------------------------------------------------

const LEAF_TAGS = new Set(['path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon']);
// Containers we recurse into to reach leaves; everything else is skipped.
const CONTAINER_TAGS = new Set(['g', 'svg', 'a']);

/**
 * Parse an SVG string into normalized-ready glyphs. Each geometry leaf becomes
 * one glyph with its element/group transforms baked into the coordinates.
 */
export function parseSvgToGlyphs(svgText: string): GlyphSource[] {
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('Invalid SVG');
  }

  const glyphs: GlyphSource[] = [];
  let visited = 0;

  const visit = (el: Element, depth: number): void => {
    if (glyphs.length >= MAX_GLYPHS || depth > MAX_DEPTH || visited > MAX_NODES) return;
    visited++;
    const tag = el.tagName.toLowerCase();

    if (LEAF_TAGS.has(tag)) {
      const raw = shapeToPath(el);
      if (raw.segments.length < 2) return; // skip empty / degenerate
      const baked = applyMatrix(raw, composeMatrix(el));
      glyphs.push({
        path: baked,
        fillRule: resolveFillRule(el),
        strokeOnly: resolveStrokeOnly(el),
      });
      return;
    }

    if (CONTAINER_TAGS.has(tag)) {
      // Recurse into containers to reach leaves; their transforms are picked up
      // by composeMatrix when each leaf is processed.
      for (let i = 0; i < el.children.length; i++) {
        if (glyphs.length >= MAX_GLYPHS) break;
        visit(el.children[i], depth + 1);
      }
    }
    // defs, symbol, clipPath, mask, text, image, use, etc. are skipped entirely.
  };

  const root = doc.documentElement;
  if (root) visit(root, 0);
  return glyphs;
}
